import { useEffect, useState } from 'react';

import getCart from '@/api/get/cart';
import addToCartKWC from '@/api/post/addToCartKWC';
import { useConfiguratorContext } from '@/kenwood/configurator/context';
import { goToPage } from '@/utils/Common/Redirects';

const useSetupStep2 = () => {
  const [
    { productsData, selectedCategory, selectedProduct, isOwner, cartData },
    {
      setSelectedCategory,
      setSelectedProduct,
      setCurrentStep,
      setCartData,
      setCartEntries,
    },
  ] = useConfiguratorContext();

  const [overviewImage, setOverviewImage] = useState('');
  const [overviewAlt, setOverviewAlt] = useState('');
  const [productTitle, setProductTitle] = useState('');

  const [attemptedAddToCart, setAttemptedAddToCart] = useState(false);

  const goToStep1 = () => {
    goToPage(`${DLG.config.encodedContextPath}/configurator/step1`);
  };

  useEffect(() => {
    setCurrentStep(2);

    // Check if the productsData is available
    if (productsData.length > 0) {
      // Populate the UI data
      if (!overviewImage || !overviewAlt || !productTitle) {
        const imageFallback = `${DLG.config.distResourcePath}/assets/images/kenwood/configurator/category-overview.png`;

        const image = isOwner
          ? selectedCategory?.image?.m?.url
          : selectedProduct?.image?.m?.url;

        const alt = isOwner
          ? selectedCategory?.image?.m?.altText
          : selectedProduct?.image?.m?.altText;

        const title = isOwner
          ? selectedCategory?.name
          : `${selectedCategory?.name} ${selectedProduct?.colorName}`;

        setOverviewImage(image || imageFallback);
        setOverviewAlt(alt);
        setProductTitle(title);
      }

      // Flow from HMC with productCode in query param
      const productCodeToAdd = new URLSearchParams(window.location.search).get(
        'productCode',
      );

      if (productCodeToAdd) {
        // Remove the productCode from the query param
        window.history.replaceState(
          {},
          document.title,
          `${DLG.config.encodedContextPath}/configurator/step2`,
        );
      }

      // Check if the product is one of the products in the productsData
      let addableProduct;
      // Find the category to which the product belongs
      productsData.forEach(category => {
        const product = category.products.find(
          p => p.code === productCodeToAdd,
        );
        if (product) {
          // Create the product object like in the step 1
          const {
            code,
            shortDescription,
            price,
            dlgConfiguratorImages,
            colorHexList,
            colorName,
          } = product;

          addableProduct = {
            code,
            description: shortDescription,
            price,
            image:
              dlgConfiguratorImages.find(i => i.format === 'product') || null,
            colorHexList,
            colorName,
            element: product,
          };

          // Populate the context
          setSelectedCategory(category);
          setSelectedProduct(addableProduct);
        }
      });

      if (addableProduct && !attemptedAddToCart && productCodeToAdd) {
        setAttemptedAddToCart(true);
        // Add the product to the cart
        addToCartKWC({
          skuCode: productCodeToAdd,
          qty: 1,
          successCB: () => {
            getCart({
              successCB: data => {
                setCartEntries(data?.entries || []);
                setCartData(data);
              },
            });
          },
          failCB: () => {
            // Redirect the user to the Step 1 if the product is properly added
            goToStep1();
          },
        });
      } else if (!attemptedAddToCart && !addableProduct && productCodeToAdd) {
        // This means the user has tried to add a product that is not in the productsData
        // Redirect the user to the Step 1 if the product is not found
        goToStep1();
      } else if (
        !attemptedAddToCart &&
        !addableProduct &&
        !productCodeToAdd &&
        !selectedCategory &&
        !selectedProduct // This is how we understand the the user comes from the Step 1
      ) {
        // This means the user just reloaded the page or got here by URL
        // Redirect the user to the Step 1
        goToStep1();
      } else if (!cartData && !attemptedAddToCart) {
        getCart({
          successCB: data => {
            setCartEntries(data?.entries || []);
            setCartData(data);
          },
        });
      }
    }
  }, [
    productsData,
    selectedCategory,
    selectedProduct,
    isOwner,
    cartData,
    attemptedAddToCart,
  ]);

  return {
    overviewImage,
    overviewAlt,
    productTitle,
  };
};

export default useSetupStep2;
